import { useSuspenseQuery } from "@tanstack/react-query";
import { Badge } from "@/components/catalyst/Badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { orpc } from "@/orpc/client";
import { formatCurrency } from "@/util/format";

interface PartnerAppsTableProps {
	partnerId: string;
}

export function PartnerAppsTable({ partnerId }: PartnerAppsTableProps) {
	const { data } = useSuspenseQuery(
		orpc.partners.appBreakdown.queryOptions({ input: { id: partnerId } }),
	);

	if (data.items.length === 0) {
		return (
			<div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50 px-6 py-12">
				<p className="text-sm text-base-content/60">
					No apps connected to this partner yet
				</p>
			</div>
		);
	}

	return (
		<div className="mt-8">
			<h3 className="text-lg font-semibold text-base-content">Apps</h3>
			<p className="mt-1 text-sm text-base-content/60">
				Performance breakdown by connected app
			</p>

			<Table className="mt-4">
				<TableHead>
					<TableRow>
						<TableHeader>App Name</TableHeader>
						<TableHeader>Status</TableHeader>
						<TableHeader className="text-right">Attributions</TableHeader>
						<TableHeader className="text-right">Earnings</TableHeader>
					</TableRow>
				</TableHead>
				<TableBody>
					{data.items.map((app) => (
						<TableRow key={app.appId}>
							<TableCell className="font-medium text-base-content">
								{app.appName}
							</TableCell>
							<TableCell>
								<Badge color={app.status ? "emerald" : "zinc"}>
									{app.status ? "Active" : "Inactive"}
								</Badge>
							</TableCell>
							<TableCell className="text-right tabular-nums text-base-content/80">
								{app.attributions.toLocaleString()}
							</TableCell>
							<TableCell className="text-right tabular-nums font-medium text-base-content">
								{formatCurrency(app.earnings, "usd")}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
